import { createStore } from 'vuex';
import Cookies from 'js-cookie';
import products from './products';
import {
  syncCartWithServer as pushCartSnapshotApi,
  fetchCartFromServer,
  convertServerCartToLocal,
  clearGuestCartSnapshot,
} from '@/services/tempCart';

/** Cola FIFO: cada addToCart rápido serializa uploads con snapshot del carrito al encolar. */
let cartPushChain = Promise.resolve();
/** Evita merges concurrentes (App.vue + página carrito) que pisan el estado local. */
let cartMergeInProgress = null;
/** Líneas del último GET al servidor (para omitir POST redundante en merge). */
let lastFetchedServerLineCount = null;
/** Evita re-merge completo si App.vue ya hidrató hace poco. */
let lastCartMergeCompletedAt = 0;
const CART_MERGE_COOLDOWN_MS = 30000;

// Helpers to handle variant-aware cart line identity
const normalizeObject = (obj) => {
  if (!obj) return '';
  // Create a stable, sorted JSON string of the object
  const keys = Object.keys(obj).sort();
  const sorted = {};
  keys.forEach((k) => { sorted[k] = obj[k]; });
  return JSON.stringify(sorted);
};

const generateLineId = (item) => {
  const baseId = item.id ?? item.product_id ?? 'unknown';
  const blister = item.blister_id ?? '';
  const variantsSig = normalizeObject(item.variants);
  return `${baseId}::${blister}::${variantsSig}`;
};

const initCart = () => {
  try {
    const raw = JSON.parse(Cookies.get('cart') || '[]');
    return (raw || []).map((it) => ({
      ...it,
      lineId: it.lineId || generateLineId(it),
    }));
  } catch {
    return [];
  }
};

const cookieOptions = {
  expires: 7,
  sameSite: 'Lax',
  secure: process.env.NODE_ENV === 'production',
  path: '/'
};

export default createStore({
  modules: {
    products
  },
  state: {
    cart: initCart(),
    auth: {
      token: localStorage.getItem('accessToken') || null,
      username: localStorage.getItem('username') || '',
      isAdmin: localStorage.getItem('isAdmin') === 'true',
      userId: localStorage.getItem('userId') || null,
      userEmail: localStorage.getItem('userEmail') || '',
      customerType: localStorage.getItem('customerType') || 'REGULAR',
    },
    syncInProgress: false,
    lastSyncTime: null,
    syncEnabled: true,
  },
  getters: {
    cartCount: (state) => {
      return state.cart.reduce((total, item) => total + item.quantity, 0);
    },
    cartTotal: (state) => {
      return state.cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    },
    cartItems: (state) => state.cart,
    isCartOpen: (state) => state.isCartOpen,
    isSyncing: (state) => state.syncInProgress,
    lastSyncTime: (state) => state.lastSyncTime,
    
    // Auth getters
    isAuthenticated: (state) => !!state.auth.token,
    currentUsername: (state) => state.auth.username,
    isAdmin: (state) => state.auth.isAdmin,
    userId: (state) => state.auth.userId,
    userEmail: (state) => state.auth.userEmail,
    customerType: (state) => state.auth.customerType,
    isDistributor: (state) => state.auth.customerType === 'DISTRIBUTOR',
  },
  mutations: {
    // Existing cart mutations
    ADD_TO_CART(state, item) {
      const lineId = generateLineId(item);
      const existingItem = state.cart.find(i => i.lineId === lineId);
      if (existingItem) {
        const incomingQty = item.quantity || 1;
        const stockValue = Number(item.stock ?? existingItem.stock);
        const hasStock = Number.isFinite(stockValue) && stockValue >= 0;
        const nextQty = existingItem.quantity + incomingQty;
        existingItem.quantity = hasStock ? Math.min(stockValue, nextQty) : nextQty;
        if (item.stock != null) {
          existingItem.stock = item.stock;
        }
      } else {
        state.cart.push({
          id: item.id,
          blister_id: item.blister_id,
          name: item.name,
          price: item.price, // Ya viene con descuento aplicado desde ProductDetails
          original_price: item.original_price, // Precio original sin descuento
          brand: item.brand,
          image: item.image,
          category_id: item.category_id,
          quantity: item.quantity || 1,
          stock: item.stock ?? null,
          currency: item.currency || 'ARS',
          discount: item.discount,
          total_discount: item.total_discount || item.discount || 0,
          variants: item.variants || {},
          has_special_promotion: item.has_special_promotion || false,
          special_promotion_name: item.special_promotion_name || null,
          price_tier: item.price_tier || 'REGULAR',
          lineId
        });
      }
      Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
    },
    REMOVE_FROM_CART(state, lineId) {
      state.cart = state.cart.filter(item => item.lineId !== lineId);
      Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
    },
    UPDATE_QUANTITY(state, { lineId, quantity }) {
      const productIndex = state.cart.findIndex(item => item.lineId === lineId);
      if (productIndex !== -1) {
        // Verificar que quantity sea un número válido
        const currentItem = state.cart[productIndex];
        const validQuantity = isNaN(quantity) ? currentItem.quantity : Number(quantity);
        // Asegurar que la cantidad mínima sea 1
        const stockValue = Number(currentItem.stock);
        const hasStock = Number.isFinite(stockValue) && stockValue >= 0;
        const cappedQuantity = hasStock ? Math.min(stockValue, validQuantity) : validQuantity;
        const finalQuantity = Math.max(1, cappedQuantity);
        
        // Crear una copia del producto y actualizar su cantidad para garantizar reactividad
        const updatedProduct = { ...currentItem, quantity: finalQuantity };
        
        // Actualizar el producto en el array del carrito
        state.cart = [
          ...state.cart.slice(0, productIndex),
          updatedProduct,
          ...state.cart.slice(productIndex + 1)
        ];
        
        // Solo removemos el producto si específicamente se indica una cantidad de 0
        if (quantity === 0) {
          state.cart = state.cart.filter(item => item.lineId !== lineId);
        }
      }
      // Actualizar la cookie con los datos del carrito
      Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
    },
    UPDATE_CART_ITEM_PRICE(state, { lineId, price, original_price, discount, total_discount, stock }) {
      const idx = state.cart.findIndex(item => item.lineId === lineId);
      if (idx !== -1) {
        state.cart = [
          ...state.cart.slice(0, idx),
          {
            ...state.cart[idx],
            price,
            original_price: original_price ?? state.cart[idx].original_price,
            discount: discount ?? state.cart[idx].discount,
            total_discount: total_discount ?? state.cart[idx].total_discount,
            ...(stock != null ? { stock } : {}),
          },
          ...state.cart.slice(idx + 1),
        ];
        Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
      }
    },
    CLEAR_CART(state) {
      state.cart = [];
      Cookies.remove('cart', cookieOptions);
      // Eliminar también el ID anónimo del carrito y snapshot en localStorage
      localStorage.removeItem('cart_anonymous_id');
      clearGuestCartSnapshot();
    },
    // Sync status mutations
    SET_SYNC_STATUS(state, status) {
      state.syncInProgress = status;
    },
    SET_LAST_SYNC_TIME(state) {
      state.lastSyncTime = Date.now();
    },
    SET_SYNC_ENABLED(state, enabled) {
      state.syncEnabled = enabled;
    },
    /** Sobrescribe cookie + estado (incluye array vacío). Usuario autenticado: servidor manda la verdad. */
    REPLACE_CART(state, cartArray) {
      const next = Array.isArray(cartArray) ? cartArray : [];
      state.cart = next;
      if (next.length === 0) {
        Cookies.remove('cart', cookieOptions);
      } else {
        Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
      }
    },
    /** Alias legado para lecturas claras donde solo queremos servidor → local cuando hay líneas */
    SET_CART_FROM_SERVER(state, serverCart) {
      if (serverCart && serverCart.length > 0) {
        state.cart = serverCart;
        Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
      }
    },
    
    // Auth mutations
    SET_AUTH_USER(state, { token, username, isAdmin, userId, userEmail, customerType }) {
      state.auth.token = token;
      state.auth.username = username;
      state.auth.isAdmin = isAdmin;
      state.auth.userId = userId || null;
      state.auth.userEmail = userEmail || '';
      state.auth.customerType = customerType || 'REGULAR';
      
      // Also update localStorage
      localStorage.setItem('accessToken', token);
      localStorage.setItem('username', username);
      localStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');
      if (userId) localStorage.setItem('userId', userId);
      if (userEmail) localStorage.setItem('userEmail', userEmail);
      localStorage.setItem('customerType', customerType || 'REGULAR');
    },
    CLEAR_AUTH(state) {
      state.auth.token = null;
      state.auth.username = '';
      state.auth.isAdmin = false;
      state.auth.userId = null;
      state.auth.userEmail = '';
      state.auth.customerType = 'REGULAR';
      
      // Clear localStorage auth data
      localStorage.removeItem('accessToken');
      localStorage.removeItem('username');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('customerType');
    }
  },
  actions: {
    // Existing cart actions with server sync
    async addToCart({ commit, dispatch, state }, product) {
      commit('ADD_TO_CART', product);
      if (state.syncEnabled) {
        await dispatch('syncCartWithServer', {
          cartSnapshot: [...state.cart],
          clearCart: false,
        });
      }
    },
    async removeFromCart({ commit, dispatch, state }, lineId) {
      commit('REMOVE_FROM_CART', lineId);
      if (state.syncEnabled) {
        await dispatch('syncCartWithServer', {
          cartSnapshot: [...state.cart],
          clearCart: state.cart.length === 0,
        });
      }
    },
    async updateQuantity({ commit, dispatch, state }, payload) {
      commit('UPDATE_QUANTITY', payload);
      if (state.syncEnabled) {
        await dispatch('syncCartWithServer', {
          cartSnapshot: [...state.cart],
          clearCart: state.cart.length === 0,
        });
      }
    },
    async clearCart({ commit, dispatch, state }) {
      commit('CLEAR_CART');
      if (state.syncEnabled) {
        await dispatch('syncCartWithServer', { clearCart: true });
      }
    },
    
    async syncCartWithServer({ commit, state }, opts = {}) {
      if (!state.syncEnabled) {
        return null;
      }

      const cartSnapshot = Array.isArray(opts.cartSnapshot)
        ? opts.cartSnapshot
        : [...state.cart];
      const syncOpts = {
        ...opts,
        clearCart: opts.clearCart === true,
      };

      cartPushChain = cartPushChain.then(async () => {
        try {
          commit('SET_SYNC_STATUS', true);
          const syncResult = await pushCartSnapshotApi(cartSnapshot, syncOpts);
          commit('SET_LAST_SYNC_TIME');
          return syncResult;
        } catch (error) {
          console.error('Error en sincronización de carrito:', error);
          return null;
        } finally {
          commit('SET_SYNC_STATUS', false);
        }
      });

      return cartPushChain;
    },
    
    async fetchCartFromServer({ commit, state }) {
      try {
        commit('SET_SYNC_STATUS', true);
        const serverCart = await fetchCartFromServer();

        if (!serverCart || serverCart.success === false) {
          lastFetchedServerLineCount = 0;
          return { serverLines: 0 };
        }

        const localCart = convertServerCartToLocal(serverCart);
        const serverLines = Array.isArray(serverCart.products) ? serverCart.products.length : 0;
        lastFetchedServerLineCount = serverLines;

        const localLines = state.cart.length;

        // Servidor con líneas → fuente de verdad. Servidor vacío + cookie con ítems → conservar local
        // (evita que un fetch vacío borre el carrito antes de que termine el sync en curso).
        if (serverLines > 0) {
          commit('REPLACE_CART', localCart);
        } else if (localLines === 0) {
          commit('REPLACE_CART', localCart);
        }
        /* Servidor vacío + cookie con ítems: no pisar; mergeLocalAndServerCarts subirá líneas al servidor. */
        return { serverLines };
      } catch (error) {
        console.error('Error al obtener carrito del servidor:', error);
        lastFetchedServerLineCount = null;
        return { serverLines: null };
      } finally {
        commit('SET_SYNC_STATUS', false);
      }
    },

    /** Al cargar la app: servidor + cookie coherente (guest sube líneas solo en cookie). */
    async hydrateCartFromServer({ dispatch, state }) {
      if (!state.syncEnabled) return;
      await dispatch('mergeLocalAndServerCarts');
    },
    
    async mergeLocalAndServerCarts({ dispatch, state }, { force = false } = {}) {
      if (!force && Date.now() - lastCartMergeCompletedAt < CART_MERGE_COOLDOWN_MS) {
        return;
      }

      if (cartMergeInProgress) {
        return cartMergeInProgress;
      }

      cartMergeInProgress = (async () => {
        const localLinesBeforeFetch = state.cart.length;
        const fetchResult = await dispatch('fetchCartFromServer');
        const serverLines = fetchResult?.serverLines ?? lastFetchedServerLineCount ?? 0;
        const localLinesAfterFetch = state.cart.length;

        const needsUpload =
          localLinesAfterFetch > 0 &&
          serverLines === 0 &&
          (localLinesBeforeFetch > 0 || localLinesAfterFetch > 0);

        if (!needsUpload) {
          lastCartMergeCompletedAt = Date.now();
          return;
        }

        if (state.syncEnabled) {
          await dispatch('syncCartWithServer', {
            cartSnapshot: [...state.cart],
            clearCart: false,
          });
        }
        lastCartMergeCompletedAt = Date.now();
      })().finally(() => {
        cartMergeInProgress = null;
      });

      return cartMergeInProgress;
    },
    
    // Auth actions
    setAuthUser({ commit }, userData) {
      commit('SET_AUTH_USER', userData);
    },
    logout({ commit }) {
      commit('CLEAR_AUTH');
    }
  }
});